import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import CloseIcon from '../../assets/icons/close_icon';
import ClickableDiv from '../../shared/components/ClickableDiv';
import { AppColors } from '../../theme/AppTheme';
import AccessCode from '../models/AccessCode';
import PPTextInput from '../../shared/components/PPTextInput';
import { useEffect, useState } from 'react';
import { Save, Update } from '@mui/icons-material';
import AccessCodeService from '../../services/accessCodeService';
import { Bars, MutatingDots } from 'react-loader-spinner';

const Row = styled('div')((props: { isNew: boolean }) => ({
    display: 'flex',
    flexDirection: 'row',
    padding: '12px 12px',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: '12px',
    backgroundColor: props.isNew ? AppColors.grey.lightest : AppColors.white,
}));

const Column = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',

}));

interface CodeRowProps {
    key: string;
    code: AccessCode;
    onDeleteClicked: (code: AccessCode) => void;
    onCodeUpdated: (code: AccessCode) => void;
    onCodeAdded: (code: AccessCode) => void;
    clearUnsavedCode: () => void;
}

const CodeRow = (props: CodeRowProps) => {
    const { key, code, onDeleteClicked, onCodeUpdated, onCodeAdded, clearUnsavedCode } = props;

    const [codeName, setCodeName] = useState<string>(code.name);
    const [codeCode, setCodeCode] = useState<string>(code.code);
    const [codeHasChanged, setCodeHasChanged] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const isNew = code.id === '';

    const saveNewcode = async (code: AccessCode) => {
        try {
            const newCode = await AccessCodeService.createCode(code);
            if (!newCode) return;
            setCodeName(newCode.name);
            setCodeCode(newCode.code);
            setCodeHasChanged(false);
            onCodeAdded(newCode);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }

    const onUpdateClicked = async (code: AccessCode) => {
        if (isNew) {
            return saveNewcode(code);
        }
        setLoading(true);
        try {
            const updatedCode = await AccessCodeService.updateCode(code);
            if (!updatedCode) return;
            onCodeUpdated(updatedCode);
            setCodeName(updatedCode.name);
            setCodeCode(updatedCode.code);
            setCodeHasChanged(false);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (codeName !== code.name || codeCode !== code.code) {
            setCodeHasChanged(true);
        } else {
            setCodeHasChanged(false);
        }
    }, [codeName, codeCode]);

    return (
        <Row isNew={isNew}>
            <Column>
                <Typography variant='h5' style={{ width: '100%', textAlign: 'left', marginRight: '8px' }}>
                    Name
                </Typography>
                <PPTextInput
                    id={`code-input-${key}`}
                    value={codeName}
                    onValueChanged={setCodeName}
                    bottomPadding='0px'
                    leadingPadding='8px'
                    fillColor={AppColors.white}
                    height='40px'
                />
            </Column>
            <Column>
                <Typography variant='h5' style={{ width: '100%', textAlign: 'left', marginRight: '8px' }}>
                    Code
                </Typography>
                <PPTextInput
                    id={`code-name-input-${key}`}
                    value={codeCode}
                    onValueChanged={setCodeCode}
                    bottomPadding='0px'
                    leadingPadding='8px'
                    fillColor={AppColors.white}
                    height='40px'
                />
            </Column>
            <div style={{ height: '100%', display: 'flex', gap: '8px', paddingBottom: '10px' }}>
                {loading &&
                    <Bars
                        height="20px"
                        width="30px"
                        color={AppColors.pink.dark}
                        ariaLabel="bars-loading"
                        visible={true}
                    />
                }

                {!loading && codeHasChanged &&
                    <ClickableDiv onClick={() => onUpdateClicked({ id: code.id, name: codeName, code: codeCode })} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Save sx={{ color: AppColors.success }} />
                    </ClickableDiv>
                }
                {!loading &&
                    <ClickableDiv onClick={() => code.id === '' ? clearUnsavedCode() : onDeleteClicked(code)} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CloseIcon color={AppColors.error} />
                    </ClickableDiv>
                }
            </div>
        </Row>
    )
}

export default CodeRow;